import type { MeetingListItem } from '@fos/shared/contracts/meetings';
import { MEETING_AI_STATUS_LABELS, MEETING_AI_STATUS_TONES, meetingPendingProposalsText } from '@fos/shared/labels';
import { FileText, MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { IconButton } from '../../../ui/Button';
import { ClientChip, ProjectChip } from '../../../ui/EntityLabel';
import { ListRow } from '../../../ui/ListRow';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../../ui/Menu';
import { Mono } from '../../../ui/Mono';
import { StatusPill } from '../../../ui/StatusPill';
import { NewTaskDialog } from '../../tasks';
import { clockText, dayText, meetingHref, participantsText } from '../meeting-format';
import { MeetingMenuItems, meetingCanAddTask, useMeetingRecordActions } from './MeetingActions';
import { useProcessMeeting } from './MeetingAiSection';
import { EditMeetingDialog } from './NewMeetingDialog';
import { TranscriptDialog } from './TranscriptPanel';

/**
 * Wiersz spotkania (lista /spotkania, profil klienta i projektu): dzień i godzina (mono), tytuł, pod nim klient
 * i projekt jako chipy (lifting 17.10.2026; w profilu klienta bez chipu klienta) i uczestnicy, po prawej to, co wymaga uwagi: propozycje zadań do przejrzenia, praca AI albo jej błąd, i menu „⋯”
 * z tymi samymi akcjami co w widoku spotkania (10.10.2026; przycisk obok linku, więc klik nie otwiera spotkania).
 */
export function MeetingRow({ meeting, hideClient = false }: { meeting: MeetingListItem; hideClient?: boolean }) {
  const people = participantsText(meeting.participants, 2);
  const client = hideClient ? null : meeting.client;
  const description =
    client || meeting.project || people ? (
      // telefon: chipy w jednej linii, uczestnicy pod nimi (obok siebie skracały się do „REA Nie…”)
      <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 md:flex-nowrap">
        {client && <ClientChip client={client} />}
        {meeting.project && <ProjectChip name={meeting.project.name} />}
        {people && <span className="min-w-0 truncate max-md:basis-full">{people}</span>}
      </span>
    ) : undefined;
  return (
    <div className="relative">
      <ListRow
        to={meetingHref(meeting.id)}
        height="l"
        leading={
          <span className="flex w-14 flex-col leading-tight">
            <span className="text-caption text-fg-muted">{dayText(meeting.date)}</span>
            <Mono className="text-body-s text-fg">{clockText(meeting.start)}</Mono>
          </span>
        }
        title={meeting.title}
        description={description}
        trailing={<MeetingRowState meeting={meeting} />}
        className="pr-10 max-md:pr-12"
      />
      <div className="absolute inset-y-0 right-0 flex items-center">
        <MeetingRowMenu meeting={meeting} />
      </div>
    </div>
  );
}

/**
 * Menu „⋯” wiersza: pozycje jak w widoku spotkania (`MeetingMenuItems`). Uprawnienia do edycji, przetworzenia
 * i udostępnienia zna dopiero szczegół spotkania, więc wczytuje go przy pierwszym najechaniu albo otwarciu menu.
 * Dialogi (edycja, transkrypcja, nowe zadanie) żyją tu, nie w menu, bo menu po wyborze pozycji się zamyka.
 */
function MeetingRowMenu({ meeting }: { meeting: MeetingListItem }) {
  const access = useAccess();
  const [wanted, setWanted] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingTranscript, setEditingTranscript] = useState(false);
  const [addingTask, setAddingTask] = useState(false);
  const detail = trpc.meetings.get.useQuery({ id: meeting.id }, { enabled: wanted });
  const actions = useMeetingRecordActions();
  const full = detail.data;
  const { run: process, pending: processing } = useProcessMeeting(full ?? meeting);
  const canAddTask = full ? meetingCanAddTask(full, access.can('tasks', 'edit')) : false;

  return (
    <>
      <Menu onOpenChange={(open) => open && setWanted(true)}>
        <MenuTrigger asChild>
          <IconButton
            icon={MoreHorizontal}
            label={`Akcje: ${meeting.title}`}
            size={32}
            iconSize={18}
            className="max-md:size-11"
            onPointerEnter={() => setWanted(true)}
            onFocus={() => setWanted(true)}
          />
        </MenuTrigger>
        <MenuContent align="end" className="w-60">
          {full ? (
            <MeetingMenuItems
              meeting={full}
              actions={actions}
              onEdit={() => setEditing(true)}
              onEditTranscript={() => setEditingTranscript(true)}
              onAddTask={() => setAddingTask(true)}
              onProcess={process}
              processing={processing}
            />
          ) : detail.isError ? (
            <MenuItem
              onSelect={(e) => {
                e.preventDefault();
                void detail.refetch();
              }}
            >
              Nie udało się wczytać. Spróbuj ponownie
            </MenuItem>
          ) : (
            <MenuItem disabled>Wczytywanie…</MenuItem>
          )}
        </MenuContent>
      </Menu>
      {full?.canEdit && <EditMeetingDialog open={editing} onOpenChange={setEditing} meeting={full} />}
      {full?.canEdit && (
        <TranscriptDialog open={editingTranscript} onOpenChange={setEditingTranscript} meeting={full} />
      )}
      {full && canAddTask && (
        <NewTaskDialog
          open={addingTask}
          onOpenChange={setAddingTask}
          defaultClientId={full.client?.id ?? null}
          defaultProjectId={full.project?.id ?? null}
        />
      )}
    </>
  );
}

function MeetingRowState({ meeting }: { meeting: MeetingListItem }) {
  if (meeting.pendingProposals > 0) {
    return (
      <StatusPill tone="progress" className="min-w-0" title={meetingPendingProposalsText(meeting.pendingProposals)}>
        <span className="hidden sm:inline">{meetingPendingProposalsText(meeting.pendingProposals)}</span>
        <span className="sm:hidden">{meeting.pendingProposals}</span>
      </StatusPill>
    );
  }
  if (meeting.aiStatus === 'queued' || meeting.aiStatus === 'failed') {
    return (
      <StatusPill tone={MEETING_AI_STATUS_TONES[meeting.aiStatus]} className="min-w-0">
        {MEETING_AI_STATUS_LABELS[meeting.aiStatus]}
      </StatusPill>
    );
  }
  if (meeting.hasTranscript) {
    return (
      <span className="hidden items-center gap-1.5 text-caption text-fg-muted md:inline-flex" title="Ma transkrypcję">
        <FileText size={14} strokeWidth={1.75} aria-hidden />
        transkrypcja
      </span>
    );
  }
  if (meeting.location) {
    return <span className="hidden min-w-0 truncate text-caption text-fg-muted md:inline">{meeting.location}</span>;
  }
  return null;
}

/** Godzina z dniem dla czytników ekranu i podpowiedzi. */
export function meetingRowLabel(meeting: MeetingListItem): string {
  return `${meeting.title}, ${dayText(meeting.date)} ${clockText(meeting.start)}`;
}
